/**
 * EU Energy Class Arrow (EU 2017/1369)
 *
 * Renders a single left-pointing arrow with the energy class letter,
 * using the official EU color scale from dark green (A+++) to red (G).
 *
 * Sanity stores ecoLabel as: "A", "APlus", "APlusPlus", "APlusPlusPlus", "B", "C", etc.
 */

export type EnergyClass = 'A+++' | 'A++' | 'A+' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'

type EnergyClassConfig = {
  label: string
  color: string
  width: number
}

/**
 * Official EU energy label colors (Commission Delegated Regulation 2019/2015).
 * Each class has a distinct color — darker green = more efficient.
 * Pre-2021 labels use A+++ → G, rescaled labels use A → G.
 *
 * @internal Exported for unit testing only.
 */
export const ENERGY_CLASSES: Record<EnergyClass, EnergyClassConfig> = {
  'A+++': {label: 'A+++', width: 100, color: '#009036'},
  'A++': {label: 'A++', width: 90, color: '#1a9640'},
  'A+': {label: 'A+', width: 80, color: '#53b031'},
  A: {label: 'A', width: 65, color: '#91c830'},
  B: {label: 'B', width: 65, color: '#c8d400'},
  C: {label: 'C', width: 65, color: '#ffed00'},
  D: {label: 'D', width: 65, color: '#f0ab00'},
  E: {label: 'E', width: 65, color: '#ec6707'},
  F: {label: 'F', width: 65, color: '#e30613'},
  G: {label: 'G', width: 65, color: '#9a0a10'},
}

/**
 * Map Sanity ecoLabel values to EU energy classes.
 *
 * @internal Exported for unit testing only.
 */
export const SANITY_TO_EU: Record<string, EnergyClass> = {
  APlusPlusPlus: 'A+++',
  'A+++': 'A+++',
  APlusPlus: 'A++',
  'A++': 'A++',
  APlus: 'A+',
  'A+': 'A+',
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G',
}

type EnergyClassArrowProps = {
  ecoLabel: string
  className?: string
}

export function EnergyClassArrow({ecoLabel, className}: EnergyClassArrowProps) {
  const energyClass = SANITY_TO_EU[ecoLabel]
  if (!energyClass) return null

  const config = ENERGY_CLASSES[energyClass]
  return (
    <span
      className={className}
      role="img"
      title={`Energy class ${config.label}`}
      aria-label={`Energy class ${config.label}`}
    >
      <svg viewBox={`0 0 ${config.width} 32`} className="h-8 w-auto" aria-hidden="true">
        {/* Arrow shape — right-pointing (EU colored bar direction) */}
        <polygon
          points={`0,0 ${config.width - 12},0 ${config.width},16 ${config.width - 12},32 0,32`}
          fill={config.color}
        />
        {/* Energy class letter — left-aligned, white */}
        <text
          x="8"
          y="23"
          textAnchor="start"
          fontFamily="Verdana, sans-serif"
          fontWeight="500"
          fontSize="18"
          fill="#fff"
        >
          {config.label.charAt(0)}
        </text>
        {config.label.length > 1 ? (
          <text
            x="20"
            y="15"
            textAnchor="start"
            fontFamily="Verdana, sans-serif"
            fontWeight="bold"
            fontSize="9"
            fill="#fff"
          >
            {config.label.slice(1)}
          </text>
        ) : null}
      </svg>
    </span>
  )
}
